import { AnswerData } from '@/components/assessment/conversational/answerTypes';

export interface StrengthCategory {
  id: string;
  title: string;
  message: string;
  priority: number;
  conditions: Array<{
    field: string;
    operator: 'equals' | 'in' | 'not_equals';
    value?: string;
    values?: string[];
  }>;
}

export const strengthsConfig: Record<string, StrengthCategory> = {
  'strategic-planning': {
    id: 'strategic-planning',
    title: 'Strategic Planning Foundation',
    message: 'You have a solid foundation with formal planning and clear timelines in place.',
    priority: 1,
    conditions: [
      {
        field: 'current-planningstatus',
        operator: 'in',
        values: ['comprehensive', 'active']
      },
      {
        field: 'business-contingencyclarity',
        operator: 'equals',
        value: 'yes-clear-plan'
      },
      {
        field: 'succession-timelineclarity',
        operator: 'in',
        values: ['very-clear', 'somewhat-clear']
      }
    ]
  },
  'business-readiness': {
    id: 'business-readiness',
    title: 'Business Readiness & Growth Mindset',
    message: 'Your practice is well-positioned with strong operational readiness and growth potential.',
    priority: 2,
    conditions: [
      {
        field: 'business-readinessstatus',
        operator: 'in',
        values: ['very-ready', 'mostly-ready']
      },
      {
        field: 'business-growthopportunities',
        operator: 'in',
        values: ['definitely', 'possibly']
      },
      {
        field: 'practice-financialperformance',
        operator: 'in',
        values: ['strong-growth', 'moderate-growth']
      }
    ]
  },
  'transition-confidence': {
    id: 'transition-confidence',
    title: 'Transition Confidence & Clarity',
    message: 'You have a clear succession path with identified successors and financing understanding.',
    priority: 5,
    conditions: [
      {
        field: 'succession-transitionconfidence',
        operator: 'in',
        values: ['very-confident', 'somewhat-confident']
      },
      {
        field: 'potential-practicesuccessor',
        operator: 'in',
        values: ['yes-internal', 'yes-external']
      },
      {
        field: 'succession-acquisitionfinance',
        operator: 'in',
        values: ['yes-understand', 'looked-into-bit']
      }
    ]
  },
  'financial-security': {
    id: 'financial-security',
    title: 'Financial Security & Valuation Awareness',
    message: 'You have strong financial positioning and realistic business valuation understanding.',
    priority: 4,
    conditions: [
      {
        field: 'business-valuationconfidence',
        operator: 'in',
        values: ['very-confident', 'somewhat-confident']
      },
      {
        field: 'succession-retirementconfidence',
        operator: 'in',
        values: ['very-confident', 'somewhat-confident']
      },
      {
        field: 'business-valuationestimation',
        operator: 'not_equals',
        value: 'no-estimate'
      }
    ]
  },
  'professional-infrastructure': {
    id: 'professional-infrastructure',
    title: 'Professional Infrastructure',
    message: 'Your legal and advisory framework appears to provide you strong succession planning support.',
    priority: 3,
    conditions: [
      {
        field: 'current-estateplan',
        operator: 'equals',
        value: 'yes-current'
      },
      {
        field: 'succession-taxpreparation',
        operator: 'equals',
        value: 'yes-recent'
      },
      {
        field: 'succession-advisoryteam',
        operator: 'in',
        values: ['yes-full-team', 'partially']
      }
    ]
  },
  'family-transition': {
    id: 'family-transition',
    title: 'Family Transition Preparedness',
    message: 'Your family succession planning shows thoughtful preparation and confidence.',
    priority: 6,
    conditions: [
      {
        field: 'successor-details.relationship',
        operator: 'equals',
        value: 'family-member'
      },
      {
        field: 'succession-familyharmony',
        operator: 'in',
        values: ['yes-absolutely', 'possibly']
      },
      {
        field: 'family-transitionconfidence',
        operator: 'in',
        values: ['very-confident', 'somewhat-confident']
      }
    ]
  }
};

function evaluateStrength(strengthKey: string, responses: AnswerData): boolean {
  const config = strengthsConfig[strengthKey];
  
  return config.conditions.every(condition => {
    let userResponse: any;
    
    // Handle nested field access (e.g., "successor-details.relationship")
    if (condition.field.includes('.')) {
      const [mainField, subField] = condition.field.split('.');
      const mainValue = responses[mainField];
      userResponse = mainValue && typeof mainValue === 'object' ? mainValue[subField] : undefined;
    } else {
      userResponse = responses[condition.field];
    }

    switch(condition.operator) {
      case 'equals':
        return userResponse === condition.value;
      case 'in':
        return condition.values?.includes(userResponse);
      case 'not_equals':
        return userResponse !== condition.value;
      default:
        return false;
    }
  });
}

export function identifyKeyStrengths(assessmentResponses: AnswerData): string[] {
  const availableStrengths: StrengthCategory[] = [];
  
  // Evaluate each strength category
  Object.keys(strengthsConfig).forEach(strengthKey => {
    if (evaluateStrength(strengthKey, assessmentResponses)) {
      availableStrengths.push(strengthsConfig[strengthKey]);
    }
  });
  
  // Sort by priority and return top 3 strength titles
  return availableStrengths
    .sort((a, b) => a.priority - b.priority)
    .slice(0, 3)
    .map(strength => strength.title);
}

export function getStrengthMessage(strengthTitle: string): string {
  const strength = Object.values(strengthsConfig).find(s => s.title === strengthTitle);
  return strength?.message || '';
}

export function getFallbackMessage(strengthsCount: number): string {
  if (strengthsCount === 0) {
    return "Assessment Complete - We've identified specific areas where targeted development will strengthen your succession planning foundation. We will build this into the Next Steps of your mySuccession journey";
  } else if (strengthsCount === 1) {
    return "We've also identified key development opportunities to build comprehensive succession readiness. We will build these into the Next Steps of your mySuccession journey.";
  } else if (strengthsCount === 2) {
    return "Additional opportunities for strengthening your succession plan have been identified. We will build these into the Next Steps of your mySuccession journey.";
  }
  return "";
}